/**
 * DASHBOARD NEWS CAROUSEL
 *
 * Displays recent News notes as draggable, navigable cards with entity context and badges.
 *
 * Changes:
 * - 2026-08-06: Avoid empty title rows so note text follows entity metadata directly.
 * - 2026-08-06: Show one-line titles, preserve body copy, and remove badge bottom margin.
 * - 2026-08-19: Move navigation outside cards, report carousel position, and suppress title-only duplication.
 * - 2026-09-02: Move the heading above the carousel surface and tighten the section's vertical density.
 * - 2026-09-02: Remove inherited heading margin and use a 4px heading-to-carousel gap.
 * - 2026-09-02: Preserve full title/body line heights inside compact cards by preventing row shrinkage.
 * - 2026-09-08: Consumes only the bounded recent News response supplied by Dashboard.
 */

import { type PointerEvent, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { Note } from '../../models/Note';
import { THEME_CLASSES } from '../../constants/themeConstants';
import NewsBadge from '../note/NewsBadge';
import TasksBadge from '../note/TasksBadge';
import { Button } from '../primitives';
import { getNoteTypeBadgeClassName } from '../note/noteTypeBadgeUtils';

interface DashboardNotesProps {
  newsNotes: Note[];
  isLoading?: boolean;
  selectedTeamFilter?: number | null;
  selectedTeamName?: string | null;
  onClearTeamFilter?: () => void;
}

const MAX_VISIBLE_NEWS_ITEMS = 12;
const CAROUSEL_DRAG_THRESHOLD = 6;

const formatNewsDate = (dateValue: Date | string) => {
  const date = new Date(dateValue);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getNoteTitle = (note: Note) => note.title?.trim() || null;

const getNoteBodyPreview = (note: Note) => {
  if (!note.content) {
    return '';
  }

  const title = getNoteTitle(note);
  const contentLines = note.content
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (title && contentLines[0]?.toLocaleLowerCase() === title.toLocaleLowerCase()) {
    const bodyLines = contentLines.slice(1);
    return bodyLines.join(' ');
  }

  return contentLines.join(' ');
};

const getInitials = (author?: string) => {
  const parts = (author || 'Unknown User').split(/\s+/).filter(Boolean);
  return parts
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const getPositiveId = (value: unknown): number | null => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

const getObjectId = (note: Note) => {
  const firstObjectId = note.objectIds?.split(',')[0]?.trim();
  return getPositiveId(firstObjectId);
};

const getNoteNavigationPath = (note: Note) => {
  const accountId = getPositiveId(note.accountId);
  const contactId = getPositiveId(note.contactId) ?? getPositiveId(note.contactid);
  const objectId = getObjectId(note);
  const objectType = note.objectType?.toLowerCase();

  if (accountId) {
    return `/accounts/${accountId}/notes/${note.id}`;
  }

  if (contactId) {
    return `/contacts/${contactId}/notes/${note.id}`;
  }

  if (objectType === 'account' && objectId) {
    return `/accounts/${objectId}/notes/${note.id}`;
  }

  if (objectType === 'contact' && objectId) {
    return `/contacts/${objectId}/notes/${note.id}`;
  }

  return null;
};

const getNoteEntity = (note: Note) => {
  const contactId = getPositiveId(note.contactId) ?? getPositiveId(note.contactid);
  const accountId = getPositiveId(note.accountId);
  const objectId = getObjectId(note);
  const objectType = note.objectType?.toLowerCase();
  const path = getNoteNavigationPath(note);

  if (note.contactName?.trim()) {
    return {
      label: `Contact: ${note.contactName.trim()}`,
      path
    };
  }

  if (note.accountName?.trim()) {
    return {
      label: `Account: ${note.accountName.trim()}`,
      path
    };
  }

  if (objectType === 'contact' && objectId) {
    return {
      label: `Contact: #${objectId}`,
      path: `/contacts/${objectId}/notes/${note.id}`
    };
  }

  if (contactId) {
    return {
      label: `Contact: #${contactId}`,
      path: `/contacts/${contactId}/notes/${note.id}`
    };
  }

  if (objectType === 'account' && objectId) {
    return {
      label: `Account: #${objectId}`,
      path: `/accounts/${objectId}/notes/${note.id}`
    };
  }

  if (accountId) {
    return {
      label: `Account: #${accountId}`,
      path: `/accounts/${accountId}/notes/${note.id}`
    };
  }

  return null;
};

export default function DashboardNotes({
  newsNotes,
  isLoading,
  selectedTeamFilter,
  selectedTeamName,
  onClearTeamFilter
}: DashboardNotesProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const railDragRef = useRef({
    hasDragged: false,
    isActive: false,
    pointerId: null as number | null,
    scrollLeft: 0,
    startX: 0
  });
  const suppressCardClickRef = useRef(false);
  const suppressCardClickTimeoutRef = useRef<number | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isRailDragging, setIsRailDragging] = useState(false);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);

  const sortedNewsNotes = useMemo(
    () => [...newsNotes]
      .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())
      .slice(0, MAX_VISIBLE_NEWS_ITEMS),
    [newsNotes]
  );

  const scrollNewsRail = (direction: 'previous' | 'next') => {
    railRef.current?.scrollBy({
      left: direction === 'next' ? 304 : -304,
      behavior: 'smooth'
    });
  };

  const updateNewsPosition = () => {
    const rail = railRef.current;
    const firstCard = rail?.firstElementChild as HTMLElement | null;

    if (!rail || !firstCard) {
      setCurrentNewsIndex(0);
      return;
    }

    const gap = Number.parseFloat(window.getComputedStyle(rail).columnGap || window.getComputedStyle(rail).gap) || 16;
    const itemWidth = firstCard.getBoundingClientRect().width + gap;
    const nextIndex = itemWidth > 0 ? Math.round(rail.scrollLeft / itemWidth) : 0;

    setCurrentNewsIndex(Math.max(0, Math.min(nextIndex, sortedNewsNotes.length - 1)));
  };

  const handleRailPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !railRef.current) {
      return;
    }

    railDragRef.current = {
      hasDragged: false,
      isActive: true,
      pointerId: event.pointerId,
      scrollLeft: railRef.current.scrollLeft,
      startX: event.clientX
    };

  };

  const handleRailPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const dragState = railDragRef.current;

    if (!dragState.isActive || dragState.pointerId !== event.pointerId || !railRef.current) {
      return;
    }

    const dragDistance = event.clientX - dragState.startX;

    if (Math.abs(dragDistance) > CAROUSEL_DRAG_THRESHOLD) {
      dragState.hasDragged = true;
      suppressCardClickRef.current = true;
      setIsRailDragging(true);

      if (!railRef.current.hasPointerCapture?.(event.pointerId)) {
        railRef.current.setPointerCapture?.(event.pointerId);
      }
    }

    if (dragState.hasDragged) {
      event.preventDefault();
      railRef.current.scrollLeft = dragState.scrollLeft - dragDistance;
    }
  };

  const endRailDrag = (event: PointerEvent<HTMLDivElement>) => {
    const dragState = railDragRef.current;

    if (!dragState.isActive || dragState.pointerId !== event.pointerId) {
      return;
    }

    if (railRef.current?.hasPointerCapture?.(event.pointerId)) {
      railRef.current.releasePointerCapture(event.pointerId);
    }

    suppressCardClickRef.current = dragState.hasDragged;
    if (dragState.hasDragged) {
      if (suppressCardClickTimeoutRef.current !== null) {
        window.clearTimeout(suppressCardClickTimeoutRef.current);
      }

      suppressCardClickTimeoutRef.current = window.setTimeout(() => {
        suppressCardClickRef.current = false;
        suppressCardClickTimeoutRef.current = null;
      }, 150);
    }

    railDragRef.current.isActive = false;
    railDragRef.current.pointerId = null;
    setIsRailDragging(false);
  };

  const renderSkeletonCard = (key: number) => (
    <div
      key={key}
      className={`h-40 w-[17rem] shrink-0 animate-pulse rounded-lg border p-3 ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background}`}
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-24 rounded bg-slate-200 dark:bg-slate-800" />
      </div>
      <div className="mb-3 h-3 w-36 rounded bg-slate-200 dark:bg-slate-800" />
      <div className="mb-3 h-5 w-44 rounded bg-slate-200 dark:bg-slate-800" />
      <div className="h-6 w-28 rounded-full bg-slate-200 dark:bg-slate-800" />
    </div>
  );

  const renderNewsCard = (note: Note) => {
    const author = note.author || 'Unknown User';
    const typeBadgeClassName = getNoteTypeBadgeClassName(note.type);
    const entity = getNoteEntity(note);
    const title = getNoteTitle(note);
    const bodyPreview = getNoteBodyPreview(note);

    const cardContent = (
      <>
        <div className="mb-2 flex shrink-0 items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
              {getInitials(author)}
            </div>
            <div className="min-w-0">
              <div className={`truncate text-sm font-semibold ${THEME_CLASSES.text.primary}`}>
                {author}
              </div>
            </div>
          </div>
          <time className={`shrink-0 text-xs font-medium ${THEME_CLASSES.text.secondary}`}>
            {formatNewsDate(note.updatedAt)}
          </time>
        </div>

        {entity?.label && (
          <div
            className="block min-h-4 w-full max-w-full shrink-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium leading-4 text-slate-600 dark:text-slate-300"
            title={entity.label}
          >
            {entity.label}
          </div>
        )}

        {title && (
          <h3 className={`m-0 shrink-0 truncate p-0 text-sm font-semibold leading-5 ${THEME_CLASSES.text.primary}`}>
            {title}
          </h3>
        )}

        {bodyPreview && (
          <p className={`mb-0 line-clamp-2 min-h-8 shrink-0 text-xs leading-4 ${THEME_CLASSES.text.secondary}`}>
            {bodyPreview}
          </p>
        )}

        <div className="mt-auto flex shrink-0 flex-wrap items-center gap-2">
          {typeBadgeClassName && (
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${typeBadgeClassName}`}>
              {note.type}
            </span>
          )}
          <NewsBadge note={note} />
          <TasksBadge note={note} />
        </div>
      </>
    );
    const cardClassName = `flex h-40 w-[17rem] shrink-0 cursor-grab select-none flex-col rounded-lg border p-3 no-underline shadow-sm transition-all duration-200 hover:no-underline active:cursor-grabbing ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} ${THEME_CLASSES.card.hover.full}`;
    const cardKey = `${note.id}-${note.accountId || ''}-${note.contactId || note.contactid || ''}`;

    if (!entity?.path) {
      return (
        <div key={cardKey} className={cardClassName}>
          {cardContent}
        </div>
      );
    }

    return (
      <Link
        key={cardKey}
        to={entity.path}
        className={cardClassName}
        onClick={(event) => {
          if (suppressCardClickRef.current) {
            event.preventDefault();
            suppressCardClickRef.current = false;
          }
        }}
      >
        {cardContent}
      </Link>
    );
  };

  return (
    <section className="space-y-1" aria-labelledby="dashboard-news-heading">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h2 id="dashboard-news-heading" className={`m-0 text-xl font-semibold ${THEME_CLASSES.text.primary}`}>
            News
          </h2>
          {selectedTeamFilter !== null && selectedTeamName && (
            <span className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
              {selectedTeamName}
              {onClearTeamFilter && (
                <button
                  type="button"
                  onClick={onClearTeamFilter}
                  className="rounded-sm p-0.5 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                  title="Clear team filter"
                >
                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Link to="/notes" className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-300 dark:hover:text-blue-200">
            View all news
          </Link>
          <Button
            onClick={() => setIsCollapsed((currentValue) => !currentValue)}
            variant="icon"
            title={isCollapsed ? 'Expand news' : 'Collapse news'}
          >
            <svg className={`h-5 w-5 transform transition-transform ${isCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </Button>
        </div>
      </div>

      {!isCollapsed && (
        <div
          className={`rounded-lg border p-3 ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} shadow-sm`}
          data-testid="news-carousel-surface"
        >
          <div
            ref={railRef}
            role="region"
            aria-label="News carousel"
            className={`flex touch-pan-y gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${isRailDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onPointerDown={handleRailPointerDown}
            onPointerMove={handleRailPointerMove}
            onPointerUp={endRailDrag}
            onPointerCancel={endRailDrag}
            onScroll={updateNewsPosition}
          >
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => renderSkeletonCard(index))
            ) : sortedNewsNotes.length > 0 ? (
              sortedNewsNotes.map(renderNewsCard)
            ) : (
              <div className={`flex h-32 w-full items-center justify-center rounded-lg border border-dashed ${THEME_CLASSES.card.border} ${THEME_CLASSES.text.secondary}`}>
                No news notes yet
              </div>
            )}
          </div>

          {sortedNewsNotes.length > 1 && (
            <div className="mt-2 flex items-center justify-between gap-3" data-testid="news-carousel-controls">
              <span className={`text-xs font-medium ${THEME_CLASSES.text.secondary}`} aria-live="polite">
                News {Math.min(currentNewsIndex + 1, sortedNewsNotes.length)} of {sortedNewsNotes.length}
              </span>
              {sortedNewsNotes.length > 4 && (
                <div className="flex items-center gap-2">
                  <Button variant="icon" title="Previous news" aria-label="Previous news" onClick={() => scrollNewsRail('previous')}>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </Button>
                  <Button variant="icon" title="Next news" aria-label="Next news" onClick={() => scrollNewsRail('next')}>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
